/**
 * RequireAuth
 *
 * Redirects to /auth/login if the current user is not logged in (userId === 'unknown').
 * Wraps the entire app so every route requires authentication.
 */

import { Navigate, useLocation } from 'react-router-dom';
import { useUserStore } from '../../stores/prioritization/userStore';
import { useSessionCheckPending } from '../../lib/authSessionState';
import { SecondFactorGate } from './SecondFactorGate';
import { guestPassForPath } from '../../lib/guestParticipant';

const UNKNOWN_USER_ID = 'unknown';

/** Route prefixes that must remain accessible without authentication. */
const PUBLIC_PREFIXES = [
  '/auth/', '/invite/', '/groups/join/', '/trust', '/brief/submit/', '/brief/view/',
  // Guest participant join (migration 660): the consumer promise is
  // "weigh in from a link" — requiring auth here would defeat it. Every
  // write behind this route is a token-scoped SECURITY DEFINER RPC.
  '/apps/session/join/',
  // Mental Map room (migration 808): the link is the access — anyone holding
  // it joins with a name. Every read and write behind this route is a
  // token-scoped SECURITY DEFINER function.
  '/map/',
  // The Plan of Record's address (migration 721): the world reads a ratified
  // record by scoped share token; members read it under RLS. Every anonymous
  // read is one SECURITY DEFINER function with identifiers stripped.
  '/record/',
];

export function RequireAuth({ children }: { children: React.ReactNode }) {
  const location = useLocation();
  const userId = useUserStore((s) => s.userId);
  const sessionCheckPending = useSessionCheckPending();

  const isPublicRoute = PUBLIC_PREFIXES.some((p) => location.pathname.startsWith(p));
  if (isPublicRoute) {
    return <>{children}</>;
  }

  // The store may be cold while a real Supabase session exists (cleared
  // storage, a new device). Wait for AuthSessionSync's first check before
  // bouncing anyone to login.
  if ((userId === UNKNOWN_USER_ID || !userId) && sessionCheckPending) {
    return null;
  }

  if (userId === UNKNOWN_USER_ID || !userId) {
    // A guest who joined by link answers at the room's own address (roadmap
    // S-8, ruling R1), so that page runs for them signed out. The pass is only
    // this browser remembering the join; every read and write behind it is a
    // token-scoped SECURITY DEFINER function with the guest secret.
    if (guestPassForPath(location.pathname)) return <>{children}</>;
    return <Navigate to="/auth/login" state={{ from: location }} replace />;
  }

  // A tenant that requires a second factor: ask for the code before anything loads.
  return <SecondFactorGate>{children}</SecondFactorGate>;
}
